/**
 * One place that turns a deal's latest Stripe invoice into what the user sees.
 *
 * Lives here rather than in DealsTable because the deals table now filters on
 * the invoice column as well as rendering it, and both must agree on the label
 * exactly — a filter offering "Past" that matched nothing because the table
 * derived the word differently would be worse than no filter at all.
 */

import type { DealMapping } from '../types/api';

/**
 * Invoice statuses that mean money is still owed.
 *
 * Stripe's status enum is exactly draft|open|paid|uncollectible|void. An
 * overdue invoice is `open` with a past `due_date`, so it is already covered
 * by `open` — see `isOverdueInvoice`.
 */
const UNPAID_INVOICE_STATUSES: ReadonlySet<string> = new Set([
  'open',
  'uncollectible',
]);

/** Display labels for Stripe's invoice statuses. Tone is decided separately —
 *  these only name the state. */
const INVOICE_STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  open: 'Open',
  draft: 'Draft',
  uncollectible: 'Uncollectible',
  void: 'Void',
};

/** How an overdue invoice reads — Stripe has no status of its own for it. */
const OVERDUE_LABEL = 'Past';

export interface InvoicePresentation {
  label: string;
  /** True when someone needs to act: overdue, or written off by Stripe. */
  isBad: boolean;
}

/**
 * Whether a deal's latest invoice is past its due date.
 *
 * Stripe never reports an overdue invoice as anything but `open`, so without
 * this an invoice weeks late renders the same neutral "Open" badge as one
 * issued today. `due_date` is null for charge_automatically invoices (Stripe
 * assigns none), which correctly reads as not overdue.
 */
export function isOverdueInvoice(deal: DealMapping, now: Date = new Date()): boolean {
  if (deal.latest_invoice_status !== 'open') return false;
  if (!deal.latest_invoice_due_date) return false;
  const due = new Date(deal.latest_invoice_due_date);
  // An unparseable date must not masquerade as overdue.
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() < now.getTime();
}

/**
 * How a deal's latest invoice reads in the table, or null when it has none.
 *
 * Unknown statuses fall through to themselves rather than being dropped, so a
 * status Stripe adds later renders as itself instead of vanishing.
 */
export function describeInvoiceStatus(
  deal: DealMapping,
  now: Date = new Date(),
): InvoicePresentation | null {
  const status = deal.latest_invoice_status;
  if (!status) return null;
  // Overdue outranks the plain status: Stripe still calls a late invoice
  // "open", and that reads as fine.
  const overdue = isOverdueInvoice(deal, now);
  return {
    label: overdue ? OVERDUE_LABEL : INVOICE_STATUS_LABELS[status] ?? status,
    // Uncollectible is Stripe giving up on the money; it belongs with overdue,
    // not with the calm states.
    isBad: overdue || status === 'uncollectible',
  };
}

/**
 * Whether a deal still owes money on ANY invoice.
 *
 * Uses the API's cache-wide `has_outstanding_invoice` rather than the newest
 * invoice's status: a deal whose latest invoice is paid can still have an
 * older open one, and a filter for money owed must not hide it. Falls back to
 * the latest status only when the backend hasn't supplied the flag.
 */
export function isUnpaidInvoice(deal: DealMapping): boolean {
  if (deal.has_outstanding_invoice !== undefined) {
    return deal.has_outstanding_invoice;
  }
  return UNPAID_INVOICE_STATUSES.has(deal.latest_invoice_status ?? '');
}
